import { Round as KeyboardArrowDownIcon } from '@manomano-internal/tamagoshi-icons/esm/material/KeyboardArrowDown';
import classnames from 'classnames';
import type React from 'react';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FunctionComponent,
} from 'react';

import { Text } from '@/core/tamagoshi/Text/Text';
import { IS_B2B } from '@/domains/core/settings/constants';

import styles from './ReadMore.module.scss';

interface Props extends React.PropsWithChildren {
  className?: string;
  textClassName?: string;
  onShowMore?: (event: MouseEvent) => void;
  textLess: string;
  textMore: string;
  lineClamp?: number;
}

const ReadMore: FunctionComponent<Props> = ({
  children,
  onShowMore = () => {},
  className,
  textLess,
  textMore,
  lineClamp = 3,
  textClassName,
}) => {
  const [isReadMoreVisible, setIsReadMoreVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef<HTMLDivElement | null>(null);

  const showReadMore = useCallback(() => {
    if (textRef.current) {
      const { offsetHeight, scrollHeight } = textRef.current;
      setIsReadMoreVisible(offsetHeight < scrollHeight);
    }
  }, [textRef]);

  useEffect(() => {
    showReadMore();
  }, [showReadMore]);

  const onClick = useCallback(
    (event: any) => {
      setIsExpanded((previous) => !previous);

      if (!isExpanded && onShowMore) {
        onShowMore(event);
      }
    },
    [isExpanded, onShowMore],
  );

  return (
    <>
      <div
        ref={textRef}
        className={classnames(styles.body, className)}
        style={{
          WebkitLineClamp: isExpanded ? 'unset' : lineClamp,
        }}
      >
        {children}
      </div>
      {isReadMoreVisible && (
        <Text
          className={classnames(textClassName, styles.text, {
            [styles.b2bText]: IS_B2B,
          })}
          onClick={onClick}
        >
          {isExpanded ? textLess : textMore}
          <KeyboardArrowDownIcon
            className={classnames(textClassName, styles.arrow, {
              [styles.arrowUp]: isExpanded,
              [styles.b2bArrow]: IS_B2B,
            })}
          />
        </Text>
      )}
    </>
  );
};

export default ReadMore;
